import React, { useState, useMemo } from 'react';
import { FormGroup } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import type { OptionType } from '../../../../Components/Controls/bx-select.types';
import { ClearIcon, FilterIcon, TickIcon } from '../../../../bombiix-icon';
import { NewSelectMultiSelect } from '../../../../Components/PIM/common';

interface SystemCanvasControlsFilterProps {
  onApplyFilter: (filters: FilterState) => void;
  filterActive: boolean;
}

interface FilterState {
  controlType: string[];
}

const CONTROL_TYPES: Array<{ labelKey: string; value: string }> = [
  { labelKey: 'COMMON.FIELD_TYPES.TEXT_BOX', value: 'text' },
  { labelKey: 'COMMON.FIELD_TYPES.DROPDOWN', value: 'dropdown' },
  { labelKey: 'COMMON.FIELD_TYPES.CHECKBOX', value: 'checkbox' },
  { labelKey: 'COMMON.FIELD_TYPES.RADIO_BUTTON', value: 'radio' },
  { labelKey: 'COMMON.FIELD_TYPES.DATE_PICKER', value: 'date-picker' },
  { labelKey: 'COMMON.FIELD_TYPES.STATUS', value: 'status' },
  { labelKey: 'COMMON.FIELD_TYPES.SOURCE', value: 'source' },
  { labelKey: 'COMMON.FIELD_TYPES.TABLE', value: 'table' },
  { labelKey: 'COMMON.FIELD_TYPES.IMAGE', value: 'image' },
  { labelKey: 'COMMON.FIELD_TYPES.CALCULATION', value: 'calculation' },
  { labelKey: 'COMMON.FIELD_TYPES.CANVAS', value: 'canvas' },
  { labelKey: 'COMMON.SECTION', value: 'section' },
  { labelKey: 'COMMON.SUB_SECTION', value: 'subSection' },
  { labelKey: 'COMMON.FIELD_TYPES.COMMENT', value: 'comment' },
];

const SystemCanvasControlsFilter: React.FC<SystemCanvasControlsFilterProps> = ({ onApplyFilter, filterActive }) => {
  const { t } = useTranslation(['common', 'custom-module-system-canvas']);

  const [filterState, setFilterState] = useState<FilterState>({
    controlType: [],
  });

  const handleFilterSelect = (val: OptionType[], key: keyof FilterState) => {
    const valueIds = val.map((valueItem) => valueItem.value);
    setFilterState((prevState) => ({
      ...prevState,
      [key]: valueIds,
    }));
  };

  const handleApplyFilter = () => {
    onApplyFilter(filterState);
  };

  const handleClearFilter = () => {
    const clearedState: FilterState = {
      controlType: [],
    };
    setFilterState(clearedState);
    onApplyFilter(clearedState);
  };

  const hasSelectedFilters = useMemo(() => {
    return filterState.controlType.length > 0;
  }, [filterState]);

  const showActiveIndicator = filterActive || hasSelectedFilters;

  const controlTypeOptions = useMemo<OptionType[]>(
    () => CONTROL_TYPES.map((controlType) => ({ label: t(controlType.labelKey, { ns: 'custom-module-system-canvas' }), value: controlType.value })),
    [t],
  );

  return (
    <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.FILTER')}>
      <OverlayTrigger
        placement="bottom-end"
        trigger="click"
        rootClose
        overlay={
          <Popover id="system-canvas-controls-filter-popover" className="custom-popover cm-filter-popover cm-filter-popover--single">
            <Popover.Content>
              <FormGroup>
                <NewSelectMultiSelect
                  isMulti
                  name="controlType"
                  options={controlTypeOptions}
                  containerClassName="w-100"
                  value={controlTypeOptions.filter((val) => filterState.controlType.includes(val.value))}
                  placeholder={t('CUSTOM_MODULE.SYSTEM_CANVAS.SELECT_CONTROL_TYPE', { ns: 'custom-module-system-canvas' })}
                  onChange={(selected) => handleFilterSelect((selected ?? []) as OptionType[], 'controlType')}
                  selectAllLabel={t('COMMON_LABEL.SELECT_ALL')}
                />
              </FormGroup>
              <div className="d-flex justify-content-end">
                <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.CLEAR')}>
                  <button
                    onClick={handleClearFilter}
                    aria-label={t('FILTER_BUTTON.CLEAR')}
                    className="bombiix-icon-container bombiix-icon-container--red"
                    type="button"
                  >
                    <ClearIcon size={24} />
                  </button>
                </CustomTooltip>
                <CustomTooltip placement="top" tooltipText={t('FILTER_BUTTON.APPLY')}>
                  <button onClick={handleApplyFilter} aria-label={t('FILTER_BUTTON.APPLY')} className="bombiix-icon-container ml-16" type="button">
                    <TickIcon size={24} />
                  </button>
                </CustomTooltip>
              </div>
            </Popover.Content>
          </Popover>
        }
      >
        <span className="filter-icon-wrapper ml-1 cursor-pointer">
          <FilterIcon size={24} />
          {showActiveIndicator && <span className="filter-active-dot" />}
        </span>
      </OverlayTrigger>
    </CustomTooltip>
  );
};

export default SystemCanvasControlsFilter;
